import { Icon, IconName } from '@usealloy/component-library';
import { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Sidebar } from '../component/Sidebar';
import { useLocalStorage } from '../component/hooks/useLocalStorage';
import {
  isApplicationQueueActive,
  isInvestigationsActive,
  isPrefixActive,
} from './routeMatchers';

// ============================================================
//     COMPONENTS
// ============================================================
/**
 * "Reviews" nav group — Applications, Investigations, Case Management,
 * and Review Queue.
 *
 * Mirrors the FE2.0 SideNav `NavList.review` structure and labels. Conditions,
 * link types (full-page `<a>` vs SPA `<Link>`), and Datadog action names are
 * ported verbatim from the legacy sidebar
 * (`~/dashboard/components/Sidebar/Sidebar.jsx`).
 */
export const ReviewGroup = () => {
  const {
    featureFlags, permissions, reviewQueueEnabled, betaCookies,
  } = useContext(UserContext);
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useLocalStorage('SideNav.Review.isOpen', true, {
    isValid: value => typeof value === 'boolean',
  });

  const showJourneysLink = featureFlags?.journeys && permissions?.['READ:Journey:journey_id'];
  const showAppQueueLink = showJourneysLink && permissions?.['READ:JourneyApplications:null'];
  const showInvestigationsLink = featureFlags?.investigations && permissions?.['READ:Investigations:null'];
  const showReviewQueueLink = permissions?.['READ:Entities:null']
    && reviewQueueEnabled
    && (featureFlags?.journeys ? !featureFlags?.hideReviewQueue : true);
  const showCaseManagementLink = permissions?.['READ:Entities:null'] && featureFlags?.caseManagement;

  const applicationRoute = featureFlags?.betaRoutesEnabled && betaCookies?.betaRoutes
    ? `${window.location.origin}/beta/journeys/applications`
    : `${window.location.origin}${routes.v3.dashboard.journeys.applications.list}`;

  const hasVisibleItem = [
    showAppQueueLink,
    showInvestigationsLink,
    showCaseManagementLink,
    showReviewQueueLink,
  ].some(Boolean);

  if (!hasVisibleItem) return null;

  return (
    <Sidebar.Group collapsible onOpenChange={setIsOpen} open={isOpen}>
      <Sidebar.GroupLabel>
        <span className="fs-unmask">Reviews</span>
      </Sidebar.GroupLabel>
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          {showAppQueueLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-application-queue"
                href={applicationRoute}
                isActive={isApplicationQueueActive(
                  routes.v3.dashboard.journeys.list,
                  pathname,
                )}
                tooltip="Applications"
              >
                <Icon icon={IconName.ApplicationReview} size="large" />
                <span className="fs-unmask">Applications</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}

          {showInvestigationsLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as={Link}
                data-dd-action-name="alloydd-sidebar-nav-investigations"
                isActive={isInvestigationsActive(
                  routes.investigations.queue,
                  pathname,
                )}
                to={routes.investigations.queue}
                tooltip="Investigations"
              >
                <Icon icon={IconName.FolderOpenInvestigation} size="large" />
                <span className="fs-unmask">Investigations</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}

          {showCaseManagementLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-case-management"
                href={`${window.location.origin}${routes.v3.dashboard.cases.list}`}
                isActive={isPrefixActive(
                  routes.v3.dashboard.cases.list,
                  pathname,
                )}
                tooltip="Case Management"
              >
                <Icon icon={IconName.CaseManagement} size="large" />
                <span className="fs-unmask">Case Management</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}

          {showReviewQueueLink && (
            <Sidebar.MenuItem>
              <Sidebar.MenuButton
                as="a"
                data-dd-action-name="alloydd-sidebar-nav-review-queue"
                href={`${window.location.origin}${routes.v3.dashboard.queues.list}`}
                isActive={isPrefixActive(
                  routes.v3.dashboard.queues.list,
                  pathname,
                )}
                tooltip="Review Queue"
              >
                <Icon icon={IconName.BallotCheck} size="large" />
                <span className="fs-unmask">Review Queue</span>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          )}
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>
  );
};
